import type { AbsoluteBounds, GroupTransform, ShapeGeometry, Transform } from './geometry.js';
import type { Effect, Fill, Line, ShapeStyleRefs } from './style.js';
import type { Hyperlink, TextBody } from './text.js';
import type { Emu } from './units.js';

export type PlaceholderType =
  | 'title'
  | 'ctrTitle'
  | 'subTitle'
  | 'body'
  | 'obj'
  | 'tbl'
  | 'chart'
  | 'clipArt'
  | 'dgm'
  | 'media'
  | 'pic'
  | 'sldNum'
  | 'dt'
  | 'ftr'
  | 'hdr'
  | 'sldImg';

export interface PlaceholderRef {
  readonly type: PlaceholderType;
  /** `idx` is what actually matches a shape to its layout placeholder; type is the fallback. */
  readonly idx?: number;
  readonly size?: 'full' | 'half' | 'quarter';
  readonly hasCustomPrompt?: boolean;
}

/** Fields every shape carries, whatever its kind. */
export interface ShapeBase {
  readonly id: string;
  readonly name: string;
  /** The shape's own transform, in its parent's coordinate space. */
  readonly transform: Transform;
  /** Resolved position on the slide, after walking every enclosing group. */
  readonly bounds: AbsoluteBounds;
  readonly placeholder?: PlaceholderRef;
  readonly hidden: boolean;
  readonly altText?: string;
  readonly title?: string;
  readonly hyperlink?: Hyperlink;
  /** True when the transform was inherited from a layout/master placeholder. */
  readonly inheritedTransform: boolean;
}

export interface AutoShape extends ShapeBase {
  readonly kind: 'shape';
  readonly geometry: ShapeGeometry;
  readonly fill: Fill;
  readonly line?: Line;
  readonly effects: readonly Effect[];
  readonly styleRefs?: ShapeStyleRefs;
  readonly text?: TextBody;
}

export interface PictureShape extends ShapeBase {
  readonly kind: 'picture';
  readonly mediaId: string;
  readonly geometry: ShapeGeometry;
  readonly fill: Fill;
  readonly line?: Line;
  readonly effects: readonly Effect[];
  readonly cropLeft: number;
  readonly cropTop: number;
  readonly cropRight: number;
  readonly cropBottom: number;
  /** Present when the picture part is linked rather than embedded — a publish blocker. */
  readonly linkedTarget?: string;
}

export interface ConnectorShape extends ShapeBase {
  readonly kind: 'connector';
  readonly geometry: ShapeGeometry;
  readonly line?: Line;
  readonly startShapeId?: string;
  readonly endShapeId?: string;
}

export interface GroupShape extends ShapeBase {
  readonly kind: 'group';
  readonly groupTransform: GroupTransform;
  readonly children: readonly Shape[];
  readonly fill: Fill;
}

export interface TableCell {
  readonly text: TextBody;
  readonly gridSpan: number;
  readonly rowSpan: number;
  /** True when this cell is absorbed by a horizontal merge and must not be drawn. */
  readonly hMerge: boolean;
  readonly vMerge: boolean;
  readonly fill: Fill;
  readonly borderLeft?: Line;
  readonly borderRight?: Line;
  readonly borderTop?: Line;
  readonly borderBottom?: Line;
  readonly marginLeft: Emu;
  readonly marginRight: Emu;
  readonly marginTop: Emu;
  readonly marginBottom: Emu;
  readonly anchor: 't' | 'ctr' | 'b';
}

export interface TableRow {
  readonly height: Emu;
  readonly cells: readonly TableCell[];
}

export interface TableShape extends ShapeBase {
  readonly kind: 'table';
  readonly columnWidths: readonly Emu[];
  readonly rows: readonly TableRow[];
  readonly firstRow: boolean;
  readonly firstColumn: boolean;
  readonly bandRow: boolean;
  readonly bandColumn: boolean;
  readonly styleId?: string;
}

/**
 * Charts, SmartArt, OLE and 3D models are not reconstructed in the player. They
 * carry their fallback drawing part so the slide can still be rendered, and they
 * force the slide's fidelity strategy to Hybrid.
 */
export interface GraphicFrameShape extends ShapeBase {
  readonly kind: 'graphicFrame';
  readonly frameKind: 'chart' | 'smartArt' | 'ole' | 'model3d' | 'unknown';
  /** Relationship id of the fallback image part, when one exists. */
  readonly fallbackMediaId?: string;
  readonly text?: TextBody;
}

export interface MediaShape extends ShapeBase {
  readonly kind: 'media';
  readonly mediaKind: 'audio' | 'video';
  readonly mediaId?: string;
  readonly posterMediaId?: string;
  readonly linkedTarget?: string;
  readonly autoPlay: boolean;
  readonly loop: boolean;
  /** 0..1. Absent means the author left the deck's default of full volume. */
  readonly volume?: number;
  /**
   * Authored mute state. It is carried rather than folded into `volume` because
   * browsers refuse to autoplay a clip with sound: an autoplaying media shape
   * that is not muted simply will not start.
   */
  readonly muted?: boolean;
}

export type Shape =
  | AutoShape
  | PictureShape
  | ConnectorShape
  | GroupShape
  | TableShape
  | GraphicFrameShape
  | MediaShape;

/** Depth-first walk over a shape tree, groups included. */
export function* walkShapes(shapes: readonly Shape[]): Generator<Shape> {
  for (const shape of shapes) {
    yield shape;
    if (shape.kind === 'group') {
      yield* walkShapes(shape.children);
    }
  }
}

export function findShapeById(shapes: readonly Shape[], id: string): Shape | undefined {
  for (const shape of walkShapes(shapes)) {
    if (shape.id === id) return shape;
  }
  return undefined;
}
